import { PLANS } from '../../server/lib/constants'
import { COLLECTION_ADD_RESULTS_LIMIT, COLLECTION_PAPER_LIMITS } from '../../config/static/pricing-plans'
import { userIsAtLeast } from './user'

/**
 * The single source of truth for "what plan is this user on?".
 *
 * Plan precedence is subtle and used to be re-derived ad hoc at every call site,
 * which let two screens disagree — most visibly a Team subscriber showing as
 * "Pro" on /pricing (SCI-2765). All of the rules live here: pure, framework-free
 * and unit-tested. UI reads the named booleans below instead of reimplementing
 * the precedence (and getting it subtly wrong).
 *
 * @param user        the redux `ui.user` object
 * @param orgLicense  the redux `ui.organizationLicense` object
 */
export const resolvePlanState = (user, orgLicense) => {
  const plan = user?.subscription?.plan ?? PLANS.free
  // A canceled (inactive) sub owns no live plan, so it must not mark any tier as
  // current — every card stays purchasable so the user can re-subscribe.
  const hasCanceled = user?.subscription?.status === 'inactive'
  const hasLicense = !!orgLicense?.hasLicense
  // The backend has shipped this flag as both camelCase and snake_case.
  const isSelfServe = !!(orgLicense?.isSelfServe ?? orgLicense?.is_self_serve)
  // A non-self-serve org license is Enterprise/institutional: their plan floors
  // up to Pro/organizational, but they aren't a self-serve Pro/Team purchaser.
  // Keep the record classification separate from its active entitlement: even a
  // lapsed Enterprise record must suppress the server-floored Pro plan, otherwise
  // dropping isEnterprise on lapse accidentally grants self-serve Pro access.
  const hasEnterpriseLicense = hasLicense && !isSelfServe
  // A lapsed license (status INACTIVE) no longer counts, mirroring hasTeamLicense —
  // so every entitlement keyed off isEnterprise (full API console via planUnlocksApi,
  // MCP token minting via planUnlocksMcpToken) drops on lapse, not just one of them.
  const isEnterprise = hasEnterpriseLicense && orgLicense?.status !== 'INACTIVE'
  // A self-serve team surfaces two ways, and they have DIFFERENT cancel semantics:
  //
  //   1. Via the self-serve org license (members, and the admin). Access comes
  //      from the license, not the personal sub — and joining a team flips a
  //      member's own subscription to `inactive`. So this path must NOT honour the
  //      `hasCanceled` guard, or those members would lose their Team plan on the
  //      pricing page (SCI-2765 review). A lapsed license (status INACTIVE) no
  //      longer grants access, so it stops counting as Team.
  //   2. Via the solo `team` subscription plan (the buyer, before/without a
  //      provisioned license). That's a personal sub, so the cancel guard applies.
  //
  // PLAN_LEVELS.team === PLAN_LEVELS.pro, so userIsAtLeast(user, PLANS.pro) can
  // gate features but CANNOT tell Pro from Team — read the plan and license here.
  const hasTeamLicense = hasLicense && isSelfServe && orgLicense?.status !== 'INACTIVE'
  const isTeam = hasTeamLicense || (!hasCanceled && plan === PLANS.team)
  const isPro = !hasCanceled && !hasEnterpriseLicense && !isTeam && userIsAtLeast(user, PLANS.pro)
  const isBasic = !hasCanceled && !hasEnterpriseLicense && !isPro && !isTeam && userIsAtLeast(user, PLANS.basic)

  return {
    plan,
    hasCanceled,
    hasLicense,
    isSelfServe,
    hasEnterpriseLicense,
    isEnterprise,
    isTeam,
    isPro,
    isBasic,
    isTeamAdmin: !!orgLicense?.isTeamAdmin,
    // Admin contact on a non-self-serve (Enterprise/institutional) license. Derived
    // server-side in checkLicense from adminContacts, which never reaches the client.
    isEnterpriseAdmin: !!orgLicense?.isEnterpriseAdmin,
    // Enterprise API access is an admin toggle on the org license (scite-api-fastapi
    // PR #1758). Solo/Pro/Team never carry it — they unlock via their plan; for
    // enterprise it gates the full console. Backend ships both cases like isSelfServe.
    // Lapse is already handled by isEnterprise (status-gated above), so planUnlocksApi's
    // `isEnterprise && apiAccessEnabled` needs no extra status check here.
    // eslint-disable-next-line camelcase
    apiAccessEnabled: !!(orgLicense?.apiAccessEnabled ?? orgLicense?.api_access_enabled),
    // API-only license (Model 2, SCI-3885): members get API access but no premium
    // platform elevation (scite-web keeps their subscription plan free at login). The
    // flag is surfaced here so platform gates can suppress premium for these members
    // while the API console stays available. Backend ships camelCase; tolerate both.
    // eslint-disable-next-line camelcase
    apiOnly: !!(orgLicense?.apiOnly ?? orgLicense?.api_only)
  }
}

// How many papers this user's collections can hold in total (SCI-3863). Takes the
// object from resolvePlanState(); precedence follows its named booleans
// (Enterprise > Pro/Team > Basic > Free).
//
// Free gets a real entry rather than flooring to Basic, because Free users do
// reach these surfaces: CreateDashboardSearchResultsModal is opened from
// SearchDropdownMenu, which gates on `user?.email` alone and not on plan. The API
// lets a free user create ONE collection of up to 100 papers (the has_free_limit
// branch of lib/dashboards/validation.py), so flooring to Basic quoted them 1,000
// when 100 was the truth (PR #8487 review).
//
// An unresolvable plan state also lands on Free — under-promising is the safe
// direction when we cannot tell what someone is entitled to.
//
// CAVEAT: this models the per-collection cap but NOT the free tier's "one
// collection only" rule, so a Free user who already has one still gets a 400 no
// matter how small the search. See SCI-3864.
export const collectionPaperLimit = ({ isEnterprise, isPro, isTeam, isBasic } = {}) => {
  if (isEnterprise) {
    return COLLECTION_PAPER_LIMITS.enterprise
  }
  if (isPro || isTeam) {
    return COLLECTION_PAPER_LIMITS.pro
  }
  if (isBasic) {
    return COLLECTION_PAPER_LIMITS.basic
  }
  return COLLECTION_PAPER_LIMITS.free
}

// How many search results one "add to collection" can actually resolve. Two
// independent ceilings apply and the lower one wins:
//
//   1. the tier's own collection cap (Free 100, Basic 1,000), and
//   2. Elasticsearch's max result window, which truncates ANY single add at
//      10,000 no matter how generous the plan.
//
// So Enterprise reports 10,000 here, not its 50,000 total — that total is real,
// but it is reached across several adds. Quoting 50,000 on a single add would
// promise 40,000 papers that never arrive.
export const collectionResultsLimit = planState =>
  Math.min(collectionPaperLimit(planState), COLLECTION_ADD_RESULTS_LIMIT)

// API access + PAYG is a self-serve Pro/Team entitlement (SCI-3608). Takes the
// object from resolvePlanState(); `isPro` already excludes Team and Enterprise,
// so OR-ing in `isTeam` gives exactly "Pro or Team".
//
// Enterprise/institutional (non-self-serve) licenses get the full console when
// their admin has turned API access on (`apiAccessEnabled` on the license,
// scite-api-fastapi PR #1758). Until then, Enterprise falls back to the MCP-only
// minting path via planUnlocksMcpToken.
// API-only licenses (SCI-3885) are suppressed to hasLicense:false for platform gates,
// so isEnterprise is false for them — but they still get the full API console. Gate the
// console on apiOnly directly so that suppression doesn't lock them out.
export const planUnlocksApi = ({ isPro, isTeam, isEnterprise, apiOnly, apiAccessEnabled } = {}) =>
  !!(isPro || isTeam || ((isEnterprise || apiOnly) && apiAccessEnabled))

// The legacy `api-console` preview flag and staging-host override predate the
// Enterprise license toggle. They may still preview the console for non-Enterprise
// accounts, but must never bypass an Enterprise license's explicit on/off setting.
export const apiConsoleAccessAllowed = (planState, previewEnabled = false) =>
  planUnlocksApi(planState) || (!!previewEnabled && !planState?.hasEnterpriseLicense)

// SCI-4060: the one-time $60 (750-call) API credit + pay-as-you-go opt-in applies
// to self-serve Pro AND Team. A solo user gets their own grant; a Team gets ONE
// grant shared across the whole account, pooled across seats (product decision,
// 2026-09). The backend grants it on REST-key mint. Enterprise/institutional
// licenses are excluded — their API billing is contract-negotiated, not this
// self-serve credit. Mirror that gate so the FE consent modal fires for exactly
// the users the backend opts in. Takes the object from resolvePlanState(); isPro
// already excludes Team/Enterprise, so OR-ing isTeam gives exactly "Pro or Team"
// (covers both a provisioned Team and a solo `team`-plan buyer).
export const planOptsIntoApiCredit = ({ isPro, isTeam } = {}) => !!(isPro || isTeam)

// Minting an MCP-scoped API token is available on every paid plan (SCI-3781).
// MCP works over OAuth on all plans; a static token is a low-friction
// alternative we don't gate behind Pro. Distinct from planUnlocksApi: general
// API access (all scopes, the full console) stays Pro/Team — paid non-Pro/Team
// plans only get the MCP-only minting path. Free stays gated. Takes the object
// from resolvePlanState().
export const planUnlocksMcpToken = ({ isBasic, isPro, isTeam, isEnterprise } = {}) =>
  !!(isBasic || isPro || isTeam || isEnterprise)
